/**
 * Opting in to push notifications for fleet messages.
 *
 * Deliberately a setting the driver switches on, not something requested at
 * launch: a permission prompt fired at a stranger gets denied once and then
 * cannot be asked again, and a browser only accepts the request from a real
 * gesture anyway.
 */

import { fetchPushKey, pushSubscribe, pushUnsubscribe } from '../auth/api';
import { getLang } from '../core/i18n';

/** Push needs all three; an iPhone only has them once installed to the Home Screen. */
export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported';
}

/** base64url VAPID key -> the Uint8Array the subscribe call wants. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * The worker cannot read localStorage, so the language is mirrored into the
 * Cache API where it can. Called on boot and whenever the language changes.
 */
export async function mirrorLangForWorker(): Promise<void> {
  if (!('caches' in window)) return;
  try {
    const cache = await caches.open('wf-prefs');
    await cache.put('/__lang', new Response(getLang()));
  } catch {
    /* private mode or no quota - the worker just falls back to English */
  }
}

/** Returns true once this device is subscribed and the server knows about it. */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const { publicKey } = await fetchPushKey();
  const reg = await navigator.serviceWorker.ready;

  // Reuse an existing subscription unless it was made for a different key -
  // re-subscribing with a stale key silently never delivers.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const existing = sub.options.applicationServerKey;
    const same =
      existing &&
      btoa(String.fromCharCode(...new Uint8Array(existing)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '') === publicKey;
    if (!same) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  await mirrorLangForWorker();
  await pushSubscribe(sub.endpoint);
  return true;
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  // Tell the server first: if unsubscribing locally succeeds but the server
  // keeps the endpoint, it goes on pushing into the void.
  await pushUnsubscribe(sub.endpoint).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

/** Is this device actually subscribed right now? */
export async function pushEnabled(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}
